// Stat-pair bars (Scored & Conceded, Penalty Record) — types + shaping (Phase 3).
//
// Both widgets are the same shape: two teams, each with two stat bars (a green
// "good" bar + a red "bad" bar), all four scaled to the max of the four so they
// are comparable within the fixture. Over a window (Last 5/10/Season) and a rate
// (Per Game / Total). Pure + framework-free; the D3 bar-width animation lives in
// the shared <StatPairBars> component. The two widgets differ only in data
// source, field names, and the two stat labels — captured by the two shapers.

/** One team's side of the pair: two values + their formatted text. */
export interface StatPairSide {
  team: string;
  first: number;
  second: number;
  firstText: string;
  secondText: string;
}

/** A stat's semantic key (the legacy `data-stat`) + its display label. */
export interface StatPairStat {
  key: string;
  label: string;
}

/** Everything <StatPairBars> needs to render: the two stat defs, both sides, scale. */
export interface StatPairView {
  /** The green (top) stat — "Scored" / "Awarded". */
  first: StatPairStat;
  /** The red (bottom) stat — "Conceded" / "Against". */
  second: StatPairStat;
  home: StatPairSide;
  away: StatPairSide;
  /** Bar-width denominator (max of the four values). */
  max: number;
}

type Window = "5" | "10" | "Season";

/** Bar width as a percentage string — matches the legacy
 *  `((val / maxVal) * 100).toFixed(1)` (guarded against a zero max). */
export function statPairPct(val: number, max: number): string {
  return ((val / (max || 1)) * 100).toFixed(1);
}

/** Value text: Total prints the integer verbatim, Per Game to 2 dp. */
export function fmtStatValue(val: number, isTotal: boolean): string {
  return isTotal ? String(val) : val.toFixed(2);
}

/** Normalise the timeline control to a data key (anything but 10/Season → 5). */
function toWindow(timeline: string): Window {
  return timeline === "10" ? "10" : timeline === "Season" ? "Season" : "5";
}

// ── Scored & Conceded ──────────────────────────────────────────────────────

export interface ScoredConcededWindow {
  scored: number;
  conceded: number;
  scoredTotal: number;
  concededTotal: number;
}
export type ScoredConcededData = Record<
  string,
  Record<Window, ScoredConcededWindow>
>;

export function shapeScoredConceded(
  data: ScoredConcededData,
  home: string,
  away: string,
  timeline: string,
  isTotal: boolean
): StatPairView | null {
  const tl = toWindow(timeline);
  const hd = data[home]?.[tl];
  const ad = data[away]?.[tl];
  if (!hd || !ad) return null;

  const hScored = isTotal ? hd.scoredTotal : hd.scored;
  const hConceded = isTotal ? hd.concededTotal : hd.conceded;
  const aScored = isTotal ? ad.scoredTotal : ad.scored;
  const aConceded = isTotal ? ad.concededTotal : ad.conceded;
  const max = Math.max(hScored, hConceded, aScored, aConceded);

  return {
    first: { key: "scored", label: "Scored" },
    second: { key: "conceded", label: "Conceded" },
    home: side(home, hScored, hConceded, isTotal),
    away: side(away, aScored, aConceded, isTotal),
    max,
  };
}

// ── Penalty Record ─────────────────────────────────────────────────────────

export interface PenaltyRecordWindow {
  awarded: number;
  against: number;
  awardedPG: number;
  againstPG: number;
}
export type PenaltyRecordData = Record<
  string,
  Record<Window, PenaltyRecordWindow>
>;

export function shapePenaltyRecord(
  data: PenaltyRecordData,
  home: string,
  away: string,
  timeline: string,
  isTotal: boolean
): StatPairView | null {
  const tl = toWindow(timeline);
  const hd = data[home]?.[tl];
  const ad = data[away]?.[tl];
  if (!hd || !ad) return null;

  const hAw = isTotal ? hd.awarded : hd.awardedPG;
  const hAg = isTotal ? hd.against : hd.againstPG;
  const aAw = isTotal ? ad.awarded : ad.awardedPG;
  const aAg = isTotal ? ad.against : ad.againstPG;
  const max = Math.max(hAw, hAg, aAw, aAg);

  return {
    first: { key: "awarded", label: "Awarded" },
    second: { key: "against", label: "Against" },
    home: side(home, hAw, hAg, isTotal),
    away: side(away, aAw, aAg, isTotal),
    max,
  };
}

function side(
  team: string,
  first: number,
  second: number,
  isTotal: boolean
): StatPairSide {
  return {
    team,
    first,
    second,
    firstText: fmtStatValue(first, isTotal),
    secondText: fmtStatValue(second, isTotal),
  };
}
